import { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import {
    alpha,
    Alert,
    Button,
    ThemeProvider,
    Box,
    Typography,
    useMediaQuery,
    Tooltip,
    Link,
    Skeleton,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useTheme } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { NumericFormat } from 'react-number-format';
import { AppVarsContext } from '../../../context/AppVarsContext';
import { buildApexTooltip } from '../../../system/apex-tooltip';
import ViMarcTableCard from '../../../system/vimarc-table-card';
import { getProgrammaticCompletionMetrics } from '../../stackadapt/completion-metrics';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, reportColors, styleReportChart } from '../../shared/report-design';
import ReportPerformanceOverview from '../../shared/ReportPerformanceOverview';

const chartPalette = { primary: [reportColors.revenue], accent: [reportColors.conversions], dual: [reportColors.revenue, reportColors.conversions], donut: reportColors.categories };

const normalizeConversionSpec = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value
            .map((entry) => {
                if (entry == null) return null;
                if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
                if (typeof entry === 'object') {
                    return entry.id || entry.action_type || entry.resourceName || entry.conversion || entry.name || entry.value || null;
                }
                return null;
            })
            .filter(Boolean);
    }
    if (typeof value === 'object') {
        const nested =
            value?.conversions ||
            value?.conversion ||
            value?.conversion_actions ||
            value?.conversionActions ||
            value?.values ||
            [];
        return normalizeConversionSpec(nested);
    }
    return [];
};

const hasConversionSpecValue = (value) => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return Number.isFinite(value);
    return false;
};

const getGoogleSpecListForService = (spec, adSetKey) => {
    if (!spec) return [];
    if (Array.isArray(spec)) return normalizeConversionSpec(spec);
    if (typeof spec === 'object') {
        const hasPaidGrant = spec.paid !== undefined || spec.grant !== undefined;
        if (hasPaidGrant) {
            const key = adSetKey === 'google_grant' ? 'grant' : 'paid';
            return normalizeConversionSpec(spec[key] || []);
        }
        return normalizeConversionSpec(spec);
    }
    return [];
};

const pickGoogleConversionSpec = (settings, adSetKey) => {
    if (!settings || typeof settings !== 'object') return null;
    const candidates = [
        settings?.conversion_defaults?.[adSetKey],
        settings?.conversion_defaults?.google_paid,
        settings?.conversion_defaults?.google_grant,
        settings?.conversion_defaults?.google,
        settings?.conversion_defaults?.adwords,
        settings?.conversionActions?.google,
        settings?.conversion_actions?.google,
        settings?.conversionSpec?.google,
        settings?.conversion_spec?.google,
        settings?.conversions?.google,
        settings?.conversionActions?.adwords,
        settings?.conversion_actions?.adwords,
        settings?.conversionSpec?.adwords,
        settings?.conversion_spec?.adwords,
        settings?.conversions?.adwords,
        settings?.conversionActions,
        settings?.conversion_actions,
        settings?.conversionSpec,
        settings?.conversion_spec,
        settings?.conversions,
        settings?.ad_sets?.[adSetKey]?.conversionActions,
        settings?.ad_sets?.[adSetKey]?.conversion_actions,
        settings?.ad_sets?.[adSetKey]?.conversionSpec,
        settings?.ad_sets?.[adSetKey]?.conversion_spec,
        settings?.ad_sets?.[adSetKey]?.conversions
    ];
    return candidates.find(hasConversionSpecValue) || null;
};

const getDefaultConversions = (settings, platform) => {
    if (!settings || typeof settings !== 'object') return [];
    try {
        if (platform === 'meta') {
            const candidates = [
                settings?.conversion_defaults?.meta,
                settings?.conversionActions?.meta,
                settings?.conversion_actions?.meta,
                settings?.conversionSpec?.meta,
                settings?.conversion_spec?.meta,
                settings?.conversions?.meta,
                settings?.conversionActions,
                settings?.conversion_actions,
                settings?.conversionSpec,
                settings?.conversion_spec,
                settings?.conversions,
                settings?.ad_sets?.meta?.conversionActions,
                settings?.ad_sets?.meta?.conversion_actions,
                settings?.ad_sets?.meta?.conversionSpec,
                settings?.ad_sets?.meta?.conversion_spec,
                settings?.ad_sets?.meta?.conversions
            ];
            const found = candidates.find((item) => Array.isArray(item) && item.length > 0) || [];
            return normalizeConversionSpec(found);
        }
        if (platform === 'google') {
            const paidRaw = pickGoogleConversionSpec(settings, 'google_paid');
            const grantRaw = pickGoogleConversionSpec(settings, 'google_grant');
            return {
                paid: getGoogleSpecListForService(paidRaw, 'google_paid'),
                grant: getGoogleSpecListForService(grantRaw, 'google_grant')
            };
        }
    } catch (error) {
        return [];
    }
    return [];
};

const formatRangeDate = (value) => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    }).format(parsed);
};

const formatRangeLabel = (start, end) => {
    const startLabel = formatRangeDate(start);
    const endLabel = formatRangeDate(end);
    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
    return startLabel || endLabel || 'Current report range';
};

const normalizePlatformName = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'display') return 'Programmatic';
    return value || 'Platform';
};

const safeNum = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getNullableNumericValue = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === undefined || value === null || value === '') continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return null;
};

const normalizePercentMetric = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    if (numeric > 0 && numeric <= 1) return numeric * 100;
    return numeric;
};

const getNumericValue = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === undefined || value === null || value === '') continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return 0;
};

const getReachMetric = (row) => getNullableNumericValue(row, [
    'REACH',
    'reach',
    'UNIQUE_REACH',
    'unique_reach'
]);

const getGenericCompletionCount = (row) => getNullableNumericValue(row, [
    'COMPLETIONS',
    'completions',
    'COMPLETED_PLAYS',
    'completed_plays',
    'COMPLETED_AUDIO_PLAYS',
    'completed_audio_plays',
    'AUDIO_COMPLETED_PLAYS',
    'audio_completed_plays',
    'AUDIO_PLAYS_COMPLETED',
    'audio_plays_completed',
    'VIDEO_COMPLETED',
    'video_completed',
    'COMPLETED_VIDEO',
    'completed_video',
    'VIDEO_COMPLETES',
    'video_completes',
    'VIDEO_COMPLETED_VIEWS',
    'video_completed_views',
    'COMPLETED_VIEWS',
    'completed_views'
]);

const getGenericCompletionRate = (row) => normalizePercentMetric(getNullableNumericValue(row, [
    'VTR',
    'vtr',
    'LTR',
    'ltr',
    'VCOMP_RATE',
    'vcomp_rate',
    'VIEW_PERCENT',
    'view_percent',
    'AUDIO_COMPLETION_RATE',
    'audio_completion_rate',
    'AUDIO_PLAYTHROUGH_RATE',
    'audio_playthrough_rate',
    'VIDEO_COMPLETION_RATE',
    'video_completion_rate',
    'VIDEO_PLAYTHROUGH_RATE',
    'video_playthrough_rate',
    'PLAYTHROUGH_RATE',
    'playthrough_rate',
    'COMPLETION_RATE',
    'completion_rate'
]));

const getCompletionRateLabel = (row, channelKey = '') => {
    if (channelKey === 'audio') return 'LTR';
    if (channelKey === 'video' || channelKey === 'ctv') return 'VTR';
    if (row?.LTR != null || row?.ltr != null || row?.AUDIO_PLAYTHROUGH_RATE != null || row?.audio_playthrough_rate != null) return 'LTR';
    if (
        row?.VTR != null
        || row?.vtr != null
        || row?.VCOMP_RATE != null
        || row?.vcomp_rate != null
        || row?.VIEW_PERCENT != null
        || row?.view_percent != null
        || row?.VIDEO_PLAYTHROUGH_RATE != null
        || row?.video_playthrough_rate != null
    ) {
        return 'VTR';
    }
    return null;
};

const resolveRowLabel = (row, keys) => {
    for (const key of keys) {
        const value = String(row?.[key] || '').trim();
        if (value) return value;
    }
    return 'Unknown';
};

const normalizePayloadRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const resolveProgrammaticSettings = (settings) => (
    settings?.ad_sets?.stackadapt
    || settings?.ad_sets?.display
    || settings?.ad_sets?.display_retail
    || null
);

const PROGRAMMATIC_CHANNELS = [
    { key: 'display', label: 'Display' },
    { key: 'native', label: 'Native' },
    { key: 'video', label: 'Video' },
    { key: 'audio', label: 'Audio' },
    { key: 'ctv', label: 'CTV' }
];

const keepActiveRow = (row) => {
    const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
    const clicks = safeNum(row?.CLICKS ?? row?.clicks);
    const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
    const cost = safeNum(row?.COST ?? row?.cost);
    const revenue = safeNum(row?.REVENUE ?? row?.revenue);
    return impressions > 0 || clicks > 0 || conversions > 0 || cost > 0 || revenue > 0;
};

const buildMetaBreakdownRows = (rows) => {
    return normalizePayloadRows(rows)
        .filter(keepActiveRow)
        .map((row, index) => {
            const impressions = getNumericValue(row, ['IMPRESSIONS', 'impressions']);
            const clicks = getNumericValue(row, ['CLICKS', 'clicks']);
            const conversions = getNumericValue(row, ['CONVERSIONS', 'conversions']);
            const reach = getReachMetric(row);
            const completedCount = getGenericCompletionCount(row);
            const completionRate = getGenericCompletionRate(row);
            const ctr = getNumericValue(row, ['CTR', 'ctr']) || (impressions > 0 ? (clicks / impressions) * 100 : 0);
            const convRate =
                getNumericValue(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']) ||
                (clicks > 0 ? (conversions / clicks) * 100 : 0);

            return {
                id: row?.ID || row?.id || row?.ADSET_ID || `meta-${index}`,
                channel: 'Meta',
                promotion: resolveRowLabel(row, ['CAMPAIGN', 'CAMPAIGN_NAME', 'campaign', 'campaign_name', 'NAME']),
                breakdown: resolveRowLabel(row, ['ADSET', 'AD_SET', 'ADSET_NAME', 'AD_SET_NAME', 'NAME', 'CAMPAIGN']),
                breakdownType: 'Ad Set',
                REACH: reach,
                IMPRESSIONS: impressions,
                CLICKS: clicks,
                CTR: ctr,
                CONVERSIONS: conversions,
                CONV_RATE: convRate,
                COMPLETED: completedCount,
                COMPLETION_RATE: completionRate,
                COMPLETION_RATE_LABEL: getCompletionRateLabel(row)
            };
        });
};

const buildGoogleBreakdownRows = (rows) => {
    const creativeRows = normalizePayloadRows(rows)
        .filter(keepActiveRow)
        .map((row, index) => {
            const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
            const clicks = safeNum(row?.CLICKS ?? row?.clicks);
            const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
            const cost = safeNum(row?.COST ?? row?.cost);
            const revenue = safeNum(row?.REVENUE ?? row?.revenue);
            return {
                ...row,
                ID: row?.ID ?? row?.AD_ID ?? `${row?.CAMPAIGN_ID || 'camp'}-${row?.ADGROUP_ID || row?.AD_GROUP_ID || 'adgroup'}-${index}`,
                IMPRESSIONS: impressions,
                CLICKS: clicks,
                CONVERSIONS: conversions,
                COST: cost,
                REVENUE: revenue
            };
        });

    const groupMap = new Map();
    creativeRows.forEach((row) => {
        const groupId =
            row?.ADGROUP_ID ||
            row?.AD_GROUP_ID ||
            row?.adgroup_id ||
            row?.ad_group_id ||
            row?.adGroupId ||
            `${row?.CAMPAIGN_ID || row?.CAMPAIGN_NAME || 'campaign'}-${row?.ADGROUP_NAME || row?.ADGROUP || 'group'}`;

        if (!groupMap.has(groupId)) {
            groupMap.set(groupId, {
                id: groupId,
                promotion: row?.CAMPAIGN_NAME || row?.CAMPAIGN || 'Campaign',
                breakdown: row?.ADGROUP_NAME || row?.ADGROUP || 'Ad Group',
                rows: []
            });
        }
        groupMap.get(groupId).rows.push(row);
    });

    return Array.from(groupMap.values()).map((group) => {
        const completionRateLabel = group.rows.map((row) => getCompletionRateLabel(row)).find(Boolean) || null;
        const totals = group.rows.reduce((acc, row) => {
            acc.impressions += safeNum(row?.IMPRESSIONS);
            acc.clicks += safeNum(row?.CLICKS);
            acc.conversions += safeNum(row?.CONVERSIONS);
            const completedCount = getGenericCompletionCount(row);
            const completionRate = getGenericCompletionRate(row);
            const reach = getReachMetric(row);
            if (reach != null) {
                acc.reach = acc.reach == null ? reach : Math.max(acc.reach, reach);
            }
            if (completedCount != null) {
                acc.completed += completedCount;
                acc.hasCompletedMetric = true;
            }
            if (completionRate != null) {
                const weight = safeNum(row?.IMPRESSIONS) > 0 ? safeNum(row?.IMPRESSIONS) : 1;
                acc.weightedCompletionRate += completionRate * weight;
                acc.completionWeight += weight;
            }
            return acc;
        }, {
            impressions: 0,
            clicks: 0,
            conversions: 0,
            reach: null,
            completed: 0,
            hasCompletedMetric: false,
            weightedCompletionRate: 0,
            completionWeight: 0
        });

        return {
            id: group.id,
            channel: 'Google',
            promotion: group.promotion,
            breakdown: group.breakdown,
            breakdownType: 'Ad Group',
            REACH: totals.reach,
            IMPRESSIONS: totals.impressions,
            CLICKS: totals.clicks,
            CTR: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
            CONVERSIONS: totals.conversions,
            CONV_RATE: totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0,
            COMPLETED: totals.hasCompletedMetric ? totals.completed : null,
            COMPLETION_RATE: totals.completionWeight > 0 ? (totals.weightedCompletionRate / totals.completionWeight) : null,
            COMPLETION_RATE_LABEL: completionRateLabel
        };
    }).filter(keepActiveRow);
};

const buildProgrammaticBreakdownRows = (payload) => {
    const channelMap = payload?.channel;
    if (!channelMap || typeof channelMap !== 'object') return [];

    return PROGRAMMATIC_CHANNELS.flatMap(({ key, label }) => {
        const campaigns = Array.isArray(channelMap?.[key]?.campaigns) ? channelMap[key].campaigns : [];

        return campaigns.map((row, index) => {
            const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
            const clicks = safeNum(row?.CLICKS ?? row?.clicks);
            const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
            const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
            const convRate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
            const name = row?.name || row?.campaign || row?.CAMPAIGN || row?.label || 'Campaign';
            const completionMetrics = getProgrammaticCompletionMetrics(row, key);
            const hasCompletionMetrics = key === 'audio' || key === 'video' || key === 'ctv';

            return {
                id: row?.id || row?.ID || row?.campaign_id || `${key}-${index}`,
                channel: label,
                promotion: name,
                breakdown: name,
                breakdownType: 'Campaign',
                rowKind: 'detail',
                REACH: getReachMetric(row),
                IMPRESSIONS: impressions,
                CLICKS: clicks,
                CTR: ctr,
                CONVERSIONS: conversions,
                CONV_RATE: convRate,
                COMPLETED: hasCompletionMetrics ? completionMetrics.completedCount : null,
                COMPLETION_RATE: hasCompletionMetrics ? completionMetrics.completionRate : null,
                COMPLETION_RATE_LABEL: getCompletionRateLabel(row, key)
            };
        });
    }).filter(keepActiveRow);
};

const SectionHeader = ({ eyebrow, title, caption, action, eyebrowSx }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1.25
        }}
    >
        <Box sx={{ display: 'grid', gap: 0.35 }}>
            {eyebrow ? (
                <Typography variant="overline" sx={eyebrowSx}>
                    {eyebrow}
                </Typography>
            ) : null}
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                {title}
            </Typography>
            {caption ? (
                <Typography variant="body2" color="text.secondary">
                    {caption}
                </Typography>
            ) : null}
        </Box>
        {action || null}
    </Box>
);

const ViMarcOverview = (props) => {
    const [overview, setOverview] = useState([]);
    const [allOverview, setAllOverview] = useState({});
    const [performanceRows, setPerformanceRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [detailWarning, setDetailWarning] = useState('');
    const { reportProfile, setLoading: setParentLoading } = props;
    const adsets = useMemo(() => {
        const raw = reportProfile?.settings;
        if (!raw) return null;
        try {
            return typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (err) {
            console.error('Invalid reportProfile.settings JSON:', err);
            return null;
        }
    }, [reportProfile?.settings]);
    const { appVars, appLoading } = useContext(AppVarsContext);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const pageShellSx = getReportPageSx(theme);
    const surfaceCardSx = getReportSurfaceSx(theme);
    const pillSx = { display: 'inline-flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', minWidth: 0 };
    const tableHeadCellSx = { bgcolor: 'background.default', color: 'text.primary', fontWeight: 700 };
    const sectionEyebrowSx = useMemo(() => ({
        letterSpacing: '0.18em',
        fontWeight: 800,
        color: alpha(theme.palette.text.primary, 0.58)
    }), [theme]);
    const panelContentSx = useMemo(() => ({
        position: 'relative',
        display: 'grid',
        gap: 1.2,
        p: { xs: 1.75, md: 2.1 }
    }), []);
    const emptyStateSx = {
        ...pillSx,
        minHeight: 140,
        justifyContent: 'center',
        textAlign: 'center',
        px: 1.6,
        py: 1.8
    };
    const tableShellSx = useMemo(() => ({
        '& .MuiTableCell-root': {
            borderColor: alpha(theme.palette.primary.main, 0.08),
            p: 1,
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.035)
        }
    }), [theme]);
    const chartBaseOptions = useMemo(() => ({
        chart: {
            background: 'transparent',
            fontFamily: 'inherit',
            toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: alpha(theme.palette.primary.main, 0.08),
            strokeDashArray: 4
        },
        states: {
            hover: { filter: { type: 'none' } },
            active: { filter: { type: 'none' } }
        }
    }), [theme]);

    const sumFields = (data) => {
        const rows = Array.isArray(data) ? data : [];
        return rows.reduce((acc, obj) => {
            const keys = ['IMPRESSIONS', 'CLICKS', 'CONVERSIONS', 'REVENUE', 'COST'];
            keys.forEach((key) => {
                acc[key] = (acc[key] || 0) + (Number(obj?.[key]) || 0);
            });
            return acc;
        }, {});
    };

    const formatCompact = (value) =>
        new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value || 0));

    const formatNumber = (value) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));

    const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

    const performanceTableColumns = useMemo(() => ([
        {
            header: 'Channel',
            accessorKey: 'channel',
            size: 120,
            Cell: ({ row }) => (
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {row.original.channel}
                </Typography>
            ),
            Footer: () => null
        },
        {
            header: 'Breakdown',
            accessorKey: 'breakdown',
            minSize: 120,
            size: 220,
            Cell: ({ row }) => (
                <Box sx={{ display: 'grid', gap: 0.16 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} title={row.original.breakdown}>
                        {row.original.breakdown}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.original.breakdownType}
                    </Typography>
                </Box>
            ),
            Footer: () => null
        },
        {
            header: 'Reach',
            accessorKey: 'REACH',
            size: 110,
            Cell: ({ row }) => (
                row.original.REACH == null
                    ? '—'
                    : <NumericFormat decimalScale={0} thousandSeparator="," value={row.original.REACH} displayType="text" />
            ),
            Footer: () => null
        },
        {
            header: 'Impressions',
            accessorKey: 'IMPRESSIONS',
            size: 120,
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={row.original.IMPRESSIONS || 0} displayType="text" />,
            Footer: () => null
        },
        {
            header: 'Clicks',
            accessorKey: 'CLICKS',
            size: 100,
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={row.original.CLICKS || 0} displayType="text" />,
            Footer: () => null
        },
        {
            header: 'CTR',
            accessorKey: 'CTR',
            size: 90,
            Cell: ({ row }) => <NumericFormat decimalScale={2} value={row.original.CTR || 0} displayType="text" suffix="%" />,
            Footer: () => null
        },
        {
            header: 'Conversions',
            accessorKey: 'CONVERSIONS',
            size: 120,
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={row.original.CONVERSIONS || 0} displayType="text" />,
            Footer: () => null
        },
        {
            header: 'Conv %',
            accessorKey: 'CONV_RATE',
            size: 95,
            Cell: ({ row }) => <NumericFormat decimalScale={2} value={row.original.CONV_RATE || 0} displayType="text" suffix="%" />,
            Footer: () => null
        },
        {
            header: 'Completed',
            accessorKey: 'COMPLETED',
            size: 120,
            Cell: ({ row }) => (
                row.original.COMPLETED == null
                    ? '—'
                    : <NumericFormat decimalScale={0} thousandSeparator="," value={row.original.COMPLETED} displayType="text" />
            ),
            Footer: () => null
        },
        {
            header: 'VTR / LTR',
            accessorKey: 'COMPLETION_RATE',
            size: 110,
            Cell: ({ row }) => (
                row.original.COMPLETION_RATE == null ? '—' : (
                    <Box sx={{ display: 'grid', gap: 0.12 }}>
                        <NumericFormat decimalScale={2} value={row.original.COMPLETION_RATE} displayType="text" suffix="%" />
                        <Typography variant="caption" color="text.secondary">
                            {row.original.COMPLETION_RATE_LABEL || 'Rate'}
                        </Typography>
                    </Box>
                )
            ),
            Footer: () => null
        }
    ]), []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        setDetailWarning('');
        if (typeof setParentLoading === 'function') setParentLoading(true);

        try {
            if (!appVars?.api_url) {
                throw new Error('Missing appVars.api_url');
            }
            if (!adsets?.ad_sets) {
                throw new Error('Missing ad_sets in reportProfile.settings');
            }

            const metaConversionSpec = getDefaultConversions(adsets, 'meta');
            const googleConversionSpec = getDefaultConversions(adsets, 'google');
            const hasMetaConfig = Boolean(adsets?.ad_sets?.meta);
            const programmaticSettings = resolveProgrammaticSettings(adsets);
            const stackadaptAid = programmaticSettings?.id || null;
            const stackadaptToken =
                reportProfile?.saKey
                || programmaticSettings?.saKey
                || programmaticSettings?.token
                || null;
            const baseReportId = sessionStorage.getItem('rid');
            const sdate = sessionStorage.getItem('sDate');
            const edate = sessionStorage.getItem('eDate');

            const raw = JSON.stringify({
                cid: baseReportId,
                sdate,
                edate,
                account: 'Paid',
               // type: [{ CAMPAIGN_TYPE: 'SEARCH' }],
                gmcid: reportProfile?.gmcid,
                metaConversionSpec,
                googleConversionSpec
            });

            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: raw,
                redirect: 'follow'
            };

            const metaRequestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: baseReportId,
                    sdate,
                    edate,
                    conversions: metaConversionSpec
                }),
                redirect: 'follow'
            };

            const googleCreativeRequestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: baseReportId,
                    sdate,
                    edate,
                    conversionSpec: googleConversionSpec
                }),
                redirect: 'follow'
            };

            const stackadaptRequestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: baseReportId,
                    sdate,
                    edate,
                    token: stackadaptToken,
                    aid: stackadaptAid
                }),
                redirect: 'follow'
            };

            const [
                overviewResult,
                metaResult,
                googleResult,
                programmaticResult
            ] = await Promise.allSettled([
                fetch(`${appVars.api_url}/platform-overview`, requestOptions).then(async (res) => {
                    if (!res.ok) {
                        throw new Error(`platform-overview ${res.status}`);
                    }
                    return res.json();
                }),
                hasMetaConfig
                    ? fetch(`${appVars.api_url}/meta/adsets`, metaRequestOptions).then(async (res) => {
                        if (!res.ok) {
                            throw new Error(`meta/adsets ${res.status}`);
                        }
                        return res.json();
                    })
                    : Promise.resolve([]),
                fetch(`${appVars.api_url}/adwords/creatives`, googleCreativeRequestOptions).then(async (res) => {
                    if (!res.ok) {
                        throw new Error(`adwords/creatives ${res.status}`);
                    }
                    return res.json();
                }),
                (stackadaptAid && stackadaptToken)
                    ? fetch(`${appVars.api_url}/stackadapt/campaigns/id`, stackadaptRequestOptions).then(async (res) => {
                        if (!res.ok) {
                            throw new Error(`stackadapt/campaigns/id ${res.status}`);
                        }
                        return res.json();
                    })
                    : Promise.resolve(null)
            ]);

            if (overviewResult.status !== 'fulfilled') {
                throw overviewResult.reason;
            }

            const response = overviewResult.value;
            const cleaned = Array.isArray(response)
                ? response
                : (response?.rows || response?.data || []);

            const detailRows = [
                ...(metaResult.status === 'fulfilled' ? buildMetaBreakdownRows(metaResult.value) : []),
                ...(googleResult.status === 'fulfilled' ? buildGoogleBreakdownRows(googleResult.value) : []),
                ...(programmaticResult.status === 'fulfilled' ? buildProgrammaticBreakdownRows(programmaticResult.value) : [])
            ].sort((a, b) => (
                (safeNum(b.IMPRESSIONS) - safeNum(a.IMPRESSIONS))
                || (safeNum(b.CLICKS) - safeNum(a.CLICKS))
                || String(a.channel || '').localeCompare(String(b.channel || ''))
            ));

            if (!stackadaptAid || !stackadaptToken) {
                console.warn('Retail awareness programmatic table skipped StackAdapt fetch due to missing config.', {
                    hasAid: Boolean(stackadaptAid),
                    hasToken: Boolean(stackadaptToken),
                    programmaticSettings
                });
            } else if (programmaticResult.status !== 'fulfilled') {
                console.warn('Retail awareness programmatic table StackAdapt fetch failed.', programmaticResult.reason);
            }

            const missingDetails = [hasMetaConfig && metaResult.status === 'rejected' ? 'Meta' : null, googleResult.status === 'rejected' ? 'Google' : null, stackadaptAid && stackadaptToken && programmaticResult.status === 'rejected' ? 'Programmatic' : null].filter(Boolean);
            if (missingDetails.length) setDetailWarning(`${missingDetails.join(', ')} promotion details could not load. The platform summary is available. Try again to reload the missing details.`);
            setPerformanceRows(detailRows);
            setAllOverview(sumFields(cleaned));
            setOverview(cleaned);
        } catch (err) {
            console.error('Awareness overview fetch error:', err);
            setLoadError('Awareness data could not load. Try again to reload this date range.');
            setOverview([]);
            setAllOverview({});
            setPerformanceRows([]);
        } finally {
            setLoading(false);
            if (typeof setParentLoading === 'function') setParentLoading(false);
        }
    }, [adsets, appVars?.api_url, reportProfile?.gmcid, reportProfile?.saKey, setParentLoading]);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (appLoading || !adsets?.ad_sets) return;
        if (!hasFetched.current) {
            fetchData();
            hasFetched.current = true;
        }
    }, [appLoading, adsets?.ad_sets, fetchData]);

    useEffect(() => {
        const handleStorageChange = () => {
            if (!appLoading && adsets?.ad_sets) {
                fetchData();
            }
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [appLoading, adsets?.ad_sets, fetchData]);

    const platformSummary = useMemo(() => {
        const rows = Array.isArray(overview) ? overview : [];
        return rows.map((row) => {
            const impressions = Number(row?.IMPRESSIONS || 0);
            const clicks = Number(row?.CLICKS || 0);
            const conversions = Number(row?.CONVERSIONS || 0);
            return {
                name: normalizePlatformName(row?.name || row?.Platform || row?.platform || 'Platform'),
                url: row?.url,
                impressions,
                clicks,
                conversions,
                ctr: impressions ? (clicks / impressions) * 100 : 0,
                convRate: clicks ? (conversions / clicks) * 100 : 0
            };
        });
    }, [overview]);

    const totals = useMemo(() => {
        const impressions = Number(allOverview?.IMPRESSIONS || 0);
        const clicks = Number(allOverview?.CLICKS || 0);
        const conversions = Number(allOverview?.CONVERSIONS || 0);
        const activePlatforms = platformSummary.filter((row) => row.impressions > 0 || row.clicks > 0 || row.conversions > 0).length;

        return {
            impressions,
            clicks,
            conversions,
            activePlatforms,
            ctr: impressions ? (clicks / impressions) * 100 : 0,
            convRate: clicks ? (conversions / clicks) * 100 : 0
        };
    }, [allOverview, platformSummary]);

    const topImpressionPlatform = useMemo(() => (
        [...platformSummary].sort((a, b) => b.impressions - a.impressions)[0] || null
    ), [platformSummary]);

    const topClickPlatform = useMemo(() => (
        [...platformSummary].sort((a, b) => b.clicks - a.clicks)[0] || null
    ), [platformSummary]);

    const platformMetric = useMemo(() => {
        if (!platformSummary.length) return { key: 'impressions', label: 'Impressions', rows: [] };
        let key = 'impressions';
        let label = 'Impressions';

        if (platformSummary.reduce((sum, row) => sum + Number(row.impressions || 0), 0) === 0) {
            key = 'clicks';
            label = 'Clicks';
        }
        if (platformSummary.reduce((sum, row) => sum + Number(row[key] || 0), 0) === 0) {
            key = 'conversions';
            label = 'Conversions';
        }

        const rows = [...platformSummary].sort((a, b) => Number(b[key] || 0) - Number(a[key] || 0));
        return { key, label, rows };
    }, [platformSummary]);

    const shareMetric = useMemo(() => {
        if (!platformSummary.length) return { key: 'clicks', label: 'Clicks', total: 0 };
        let key = 'clicks';
        let label = 'Clicks';
        let total = platformSummary.reduce((sum, row) => sum + Number(row.clicks || 0), 0);

        if (total === 0) {
            key = 'impressions';
            label = 'Impressions';
            total = platformSummary.reduce((sum, row) => sum + Number(row.impressions || 0), 0);
        }
        if (total === 0) {
            key = 'conversions';
            label = 'Conversions';
            total = platformSummary.reduce((sum, row) => sum + Number(row.conversions || 0), 0);
        }

        return { key, label, total };
    }, [platformSummary]);

    const shareSeries = useMemo(
        () => platformSummary.map((row) => Number(row[shareMetric.key] || 0)),
        [platformSummary, shareMetric.key]
    );

    const shareLabels = useMemo(
        () => platformSummary.map((row) => row.name),
        [platformSummary]
    );

    const shareBreakdown = useMemo(() => {
        if (!platformSummary.length || !shareMetric.total) return [];
        return [...platformSummary]
            .map((row) => ({
                name: row.name,
                value: Number(row[shareMetric.key] || 0),
                percent: shareMetric.total ? (Number(row[shareMetric.key] || 0) / shareMetric.total) * 100 : 0
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 4);
    }, [platformSummary, shareMetric]);

    const awarenessChart = useMemo(() => {
        if (!platformMetric.rows.length) return null;
        const topRows = platformMetric.rows.slice(0, 6);
        return {
            series: [
                {
                    name: platformMetric.label,
                    data: topRows.map((row) => Number(row[platformMetric.key] || 0))
                }
            ],
            options: {
                ...chartBaseOptions,
                chart: { ...chartBaseOptions.chart, type: 'bar' },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 8 } },
                colors: chartPalette.primary,
                xaxis: {
                    categories: topRows.map((row) => row.name),
                    labels: {
                        formatter: (val) => formatCompact(val),
                        style: { colors: alpha(theme.palette.text.primary, 0.62) }
                    }
                },
                yaxis: {
                    labels: { style: { colors: alpha(theme.palette.text.primary, 0.66) } }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatNumber(val)
                })
            }
        };
    }, [chartBaseOptions, platformMetric, theme]);

    const shareChart = useMemo(() => {
        if (!shareSeries.length || shareSeries.every((value) => Number(value) === 0)) return null;
        return {
            series: shareSeries,
            options: {
                ...chartBaseOptions,
                labels: shareLabels,
                chart: { ...chartBaseOptions.chart, type: 'donut' },
                legend: {
                    position: 'bottom',
                    labels: { colors: alpha(theme.palette.text.primary, 0.7) }
                },
                stroke: { colors: [alpha('#ffffff', 0.8)], width: 4 },
                plotOptions: { pie: { donut: { size: '68%' } } },
                colors: chartPalette.donut,
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatNumber(val)
                })
            }
        };
    }, [chartBaseOptions, shareSeries, shareLabels, theme]);

    const reachAndClickChart = useMemo(() => {
        if (!platformSummary.length) return null;
        return {
            series: [
                { name: 'Impressions', data: platformSummary.map((row) => row.impressions) },
                { name: 'Clicks', data: platformSummary.map((row) => row.clicks) }
            ],
            options: {
                ...chartBaseOptions,
                chart: { ...chartBaseOptions.chart, type: 'bar' },
                plotOptions: { bar: { columnWidth: '52%', borderRadius: 7 } },
                colors: chartPalette.dual,
                xaxis: {
                    categories: platformSummary.map((row) => row.name),
                    labels: {
                        rotate: -15,
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                yaxis: {
                    labels: {
                        formatter: (val) => formatCompact(val),
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'left',
                    labels: { colors: alpha(theme.palette.text.primary, 0.7) }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatNumber(val)
                })
            }
        };
    }, [chartBaseOptions, platformSummary, theme]);

    const ctrChart = useMemo(() => {
        if (!platformSummary.length) return null;
        return {
            series: [{ name: 'CTR', data: platformSummary.map((row) => row.ctr) }],
            options: {
                ...chartBaseOptions,
                chart: { ...chartBaseOptions.chart, type: 'bar' },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 8 } },
                colors: chartPalette.accent,
                xaxis: {
                    categories: platformSummary.map((row) => row.name),
                    labels: {
                        formatter: (val) => `${Number(val).toFixed(1)}%`,
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                yaxis: {
                    labels: { style: { colors: alpha(theme.palette.text.primary, 0.66) } }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => `${Number(val).toFixed(2)}%`
                })
            }
        };
    }, [chartBaseOptions, platformSummary, theme]);

    const volumeSnapshotChart = useMemo(() => {
        const labels = ['Impressions', 'Clicks', 'Conversions'];
        const data = [totals.impressions, totals.clicks, totals.conversions];
        if (!data.some((val) => Number(val) > 0)) return null;
        return {
            series: [{ name: 'Total', data }],
            options: {
                ...chartBaseOptions,
                chart: { ...chartBaseOptions.chart, type: 'bar' },
                plotOptions: { bar: { columnWidth: '55%', borderRadius: 7 } },
                colors: chartPalette.primary,
                xaxis: {
                    categories: labels,
                    labels: {
                        rotate: -10,
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                yaxis: {
                    labels: {
                        formatter: (val) => formatCompact(val),
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatNumber(val)
                })
            }
        };
    }, [chartBaseOptions, totals, theme]);

    const engagementSnapshotChart = useMemo(() => {
        const labels = ['CTR', 'Conv Rate'];
        const data = [totals.ctr, totals.convRate];
        if (!data.some((val) => Number(val) > 0)) return null;
        return {
            series: [{ name: 'Rate', data }],
            options: {
                ...chartBaseOptions,
                chart: { ...chartBaseOptions.chart, type: 'bar' },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 8 } },
                colors: chartPalette.accent,
                xaxis: {
                    categories: labels,
                    labels: {
                        formatter: (val) => `${Number(val).toFixed(1)}%`,
                        style: { colors: alpha(theme.palette.text.primary, 0.64) }
                    }
                },
                yaxis: {
                    labels: { style: { colors: alpha(theme.palette.text.primary, 0.66) } }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => `${Number(val).toFixed(2)}%`
                })
            }
        };
    }, [chartBaseOptions, totals, theme]);

    const rangeLabel = formatRangeLabel(
        sessionStorage.getItem('sDate'),
        sessionStorage.getItem('eDate')
    );

    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                <ReportPerformanceOverview
                    eyebrow="Retail awareness"
                    title="From visibility to response"
                    description="Compare channel delivery, site traffic, and recorded actions across your retail media mix."

                    loading={loading}
                    stages={[
                        { label: 'Visibility', description: 'Exposure across active platforms.', metrics: [
                            { label: 'Impressions', value: loadError ? '—' : formatNumber(totals.impressions), detail: 'Delivered impressions' },
                            { label: 'Active platforms', value: loadError ? '—' : formatNumber(totals.activePlatforms), detail: 'Platforms with reported activity' }
                        ] },
                        { label: 'Traffic', description: 'Visits generated by your advertising.', metrics: [
                            { label: 'Clicks', value: loadError ? '—' : formatNumber(totals.clicks), detail: 'Traffic generated' },
                            { label: 'CTR', value: loadError ? '—' : formatPercent(totals.ctr), detail: 'Clicks / impressions' }
                        ] },
                        { label: 'Response', description: 'Recorded actions following a click.', metrics: [
                            { label: 'Conversions', value: loadError ? '—' : formatNumber(totals.conversions), detail: 'Recorded conversions' },
                            { label: 'Conversion rate', value: loadError ? '—' : formatPercent(totals.convRate), detail: 'Conversions / clicks' }
                        ] }
                    ]}
                    highlights={loading || loadError ? [] : [
                        { label: 'Impression leader', title: topImpressionPlatform?.name || (loadError ? 'Unavailable' : 'No activity'), value: topImpressionPlatform ? formatNumber(topImpressionPlatform.impressions) : '—', detail: 'Delivered impressions in this period.' },
                        { label: 'Click leader', title: topClickPlatform?.name || (loadError ? 'Unavailable' : 'No activity'), value: topClickPlatform ? formatNumber(topClickPlatform.clicks) : '—', detail: 'Traffic from the leading platform.' }
                    ]}
                />
                {loadError ? <Alert severity="error" action={<Button color="inherit" onClick={fetchData}>Try again</Button>}>{loadError}</Alert> : null}
                {detailWarning ? <Alert severity="warning" action={<Button color="inherit" onClick={fetchData}>Try again</Button>}>{detailWarning}</Alert> : null}
                {!loading && !loadError && !overview.length ? <Alert severity="info">No awareness activity was returned for this date range. Choose a wider date range to review earlier activity.</Alert> : null}
                {loading ? <Skeleton variant="rounded" height={340} sx={surfaceCardSx} /> : loadError ? null : <>
                <Card variant="outlined" sx={surfaceCardSx}>
                    <CardContent sx={panelContentSx}>
                        <SectionHeader
                            eyebrow="Cross-Channel Breakdown"
                            title="Performance Table"
                            caption="Programmatic campaigns, Meta ad sets, and Google ad groups with reach, response, and completion coverage in one table."
                            eyebrowSx={sectionEyebrowSx}
                            action={(
                                <Box sx={pillSx}>
                                    <Typography variant="caption" color="text.secondary">Rows</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{performanceRows.length}</Typography>
                                </Box>
                            )}
                        />
                        {performanceRows.length ? (
                            <Box sx={{ mt: 0.5 }}>
                                <ViMarcTableCard
                                    instanceKey="retail-awareness-cross-channel"
                                    title="Performance Table"
                                    data={performanceRows}
                                    columns={performanceTableColumns}
                                    appendDefaultMetrics={false}
                                    compact={false}
                                    getRowId={(row, index) => row.id || `${row.channel}-${row.breakdown}-${index}`}
                                />
                            </Box>
                        ) : (
                            <Box sx={emptyStateSx}>
                                <Typography variant="body2" color="text.secondary">
                                    No promotion details were returned for this date range. Choose a wider date range to review earlier promotions.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                            xs: '1fr',
                            lg: '1.15fr 0.85fr'
                        }
                    }}
                >
                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Channel Ranking"
                                title="Platform Reach Snapshot"
                                caption={`Ranked by ${platformMetric.label}`}
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {awarenessChart ? (
                                <ReactApexChart
                                    options={styleReportChart(theme, awarenessChart.options)}
                                    series={awarenessChart.series}
                                    type="bar"
                                    height={300}
                                />
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No awareness ranking available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Distribution"
                                title="Platform Share"
                                caption={`Share of ${shareMetric.label}`}
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {shareChart ? (
                                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(0, 1.08fr)' }, alignItems: 'center' }}>
                                    <ReactApexChart
                                        options={styleReportChart(theme, shareChart.options)}
                                        series={shareChart.series}
                                        type="donut"
                                        height={isDesktop ? 280 : 250}
                                    />
                                    <Box sx={{ display: 'grid', gap: 1.3 }}>
                                        {shareBreakdown.map((row, index) => (
                                            <Box key={`${row.name}-${index}`} sx={{ display: 'grid', gap: 0.48 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={row.name}>
                                                        {row.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                                        {row.percent.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ height: 8, borderRadius: 999, background: alpha(theme.palette.primary.main, 0.08) }}>
                                                    <Box
                                                        sx={{
                                                            height: '100%',
                                                            width: `${row.percent}%`,
                                                            borderRadius: 999,
                                                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {shareMetric.label}: {formatNumber(row.value)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No platform distribution available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))'
                        }
                    }}
                >
                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Media Balance"
                                title="Awareness Mix by Platform"
                                caption="Impressions versus clicks"
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {reachAndClickChart ? (
                                <ReactApexChart
                                    options={styleReportChart(theme, reachAndClickChart.options)}
                                    series={reachAndClickChart.series}
                                    type="bar"
                                    height={295}
                                />
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No platform mix available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Engagement"
                                title="CTR by Platform"
                                caption="Click-through rate by channel"
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {ctrChart ? (
                                <ReactApexChart
                                    options={styleReportChart(theme, ctrChart.options)}
                                    series={ctrChart.series}
                                    type="bar"
                                    height={295}
                                />
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No CTR data available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))'
                        }
                    }}
                >
                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Executive Snapshot"
                                title="Overall Volume Snapshot"
                                caption="High-level reach and action totals"
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {volumeSnapshotChart ? (
                                <ReactApexChart
                                    options={styleReportChart(theme, volumeSnapshotChart.options)}
                                    series={volumeSnapshotChart.series}
                                    type="bar"
                                    height={235}
                                />
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No volume data available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    <Card variant="outlined" sx={surfaceCardSx}>
                        <CardContent sx={panelContentSx}>
                            <SectionHeader
                                eyebrow="Executive Snapshot"
                                title="Overall Engagement Snapshot"
                                caption="Rates across the active report mix"
                                eyebrowSx={sectionEyebrowSx}
                            />
                            {engagementSnapshotChart ? (
                                <ReactApexChart
                                    options={styleReportChart(theme, engagementSnapshotChart.options)}
                                    series={engagementSnapshotChart.series}
                                    type="bar"
                                    height={235}
                                />
                            ) : (
                                <Box sx={emptyStateSx}>
                                    <Typography variant="body2" color="text.secondary">
                                        No engagement data available yet.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                    {isDesktop ? (
                        <Card variant="outlined" sx={surfaceCardSx}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ px: { xs: 1.75, md: 2.1 }, py: 1.75, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}` }}>
                                    <SectionHeader
                                        eyebrow="Channel Detail"
                                        title="Platform Summary"
                                        caption="Awareness and engagement by platform"
                                        eyebrowSx={sectionEyebrowSx}
                                        action={(
                                            <Box sx={pillSx}>
                                                <Typography variant="caption" color="text.secondary">Table view</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800 }}>{platformSummary.length} rows</Typography>
                                            </Box>
                                        )}
                                    />
                                </Box>
                                <TableContainer sx={tableShellSx}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={tableHeadCellSx}>Platform</TableCell>
                                                <TableCell align="right" sx={tableHeadCellSx}>Impressions</TableCell>
                                                <TableCell align="right" sx={tableHeadCellSx}>Clicks</TableCell>
                                                <TableCell align="right" sx={tableHeadCellSx}>CTR</TableCell>
                                                <TableCell align="right" sx={tableHeadCellSx}>Conversions</TableCell>
                                                <TableCell align="right" sx={tableHeadCellSx}>Conv Rate</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {platformSummary.map((row, index) => (
                                                <TableRow
                                                    key={`${row.name}-${index}`}
                                                    hover
                                                    sx={{
                                                        '&:nth-of-type(odd)': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.02)
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'grid', gap: 0.35 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap title={row.name}>
                                                                {row.name}
                                                            </Typography>

                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{formatNumber(row.impressions)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.clicks)}</TableCell>
                                                    <TableCell align="right">{row.ctr.toFixed(2)}%</TableCell>
                                                    <TableCell align="right">{formatNumber(row.conversions)}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip
                                                            title={
                                                                <Box>
                                                                    <Typography fontWeight={900}>Conversion Rate</Typography>
                                                                    <Typography>{'Conversions divided by clicks'}</Typography>
                                                                </Box>
                                                            }
                                                        >
                                                            <span>{row.convRate.toFixed(2)}%</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    ) : (
                        platformSummary.map((row, index) => (
                            <Card key={`${row.name}-${index}`} variant="outlined" sx={surfaceCardSx}>
                                <CardContent sx={panelContentSx}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                            <Typography variant="overline" sx={{ ...sectionEyebrowSx, fontSize: '0.62rem' }}>
                                                Platform
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap title={row.name}>
                                                {row.name}
                                            </Typography>
                                        </Box>
                                        {row.url ? (
                                            <Link
                                                href={row.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                variant="caption"
                                                sx={{ color: theme.palette.primary.main, fontWeight: 700 }}
                                            >
                                                View platform
                                            </Link>
                                        ) : null}
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                            <Typography variant="caption" color="text.secondary">Impressions</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(row.impressions)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                            <Typography variant="caption" color="text.secondary">Clicks</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(row.clicks)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                            <Typography variant="caption" color="text.secondary">CTR</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.ctr.toFixed(2)}%</Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                            <Typography variant="caption" color="text.secondary">Conversions</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(row.conversions)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ ...pillSx, justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Conversion rate
                                        </Typography>
                                        <Tooltip
                                            title={
                                                <Box>
                                                    <Typography fontWeight={900}>Conversion Rate</Typography>
                                                    <Typography>{'Conversions divided by clicks'}</Typography>
                                                </Box>
                                            }
                                        >
                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                                {row.convRate.toFixed(2)}%
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Box>
                </>}
            </Box>
        </Box></ThemeProvider>
    );
};

export default ViMarcOverview;
